import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import type { ComposerAccess } from '../model/composer';
import { insertHandOff } from '../model/insertHandOff';
import { recordHandOff, useHandOffLink } from '../state/handOffStore';

type PendingReplace = {
  replyDraftId: string;
  composedText: string;
  text: string;
  start: number;
};

/**
 * Puts a handed-off reply into the composer and remembers which draft it came
 * from, so the send can name it.
 *
 * `place` resolves to `placed` when the composer was set, or `confirm` when an
 * earlier hand-off was edited and replacing it needs the expert's yes:
 * `confirmReplace` then applies it, and `declineReplace` leaves the composer
 * as it is. The composer is read when `place` runs, not when it was last
 * rendered, because the hand-off arrives after a check that takes seconds.
 */
export function useHandOffToComposer(conversationId: string, composer: ComposerAccess) {
  const link = useHandOffLink(conversationId);

  const composerRef = useRef(composer);

  const linkRef = useRef(link);

  useLayoutEffect(() => {
    composerRef.current = composer;

    linkRef.current = link;
  });

  const [pendingReplace, setPendingReplace] = useState<PendingReplace | null>(null);

  const apply = useCallback(
    ({ replyDraftId, composedText, text, start }: PendingReplace) => {
      composerRef.current.setText(text);

      recordHandOff(conversationId, { replyDraftId, text: composedText, start });
    },
    [conversationId]
  );

  const place = useCallback(
    (replyDraftId: string, composedText: string): 'placed' | 'confirm' => {
      const previous = linkRef.current;

      const placement = insertHandOff({
        composerText: composerRef.current.getText(),
        tokenStart: composerRef.current.getTokenStart(),
        composedText,
        previous: previous ? { text: previous.text, start: previous.start } : null,
      });

      const next = { replyDraftId, composedText, text: placement.text, start: placement.start };

      if (placement.kind === 'confirm_replace') {
        setPendingReplace(next);

        return 'confirm';
      }

      apply(next);

      return 'placed';
    },
    [apply]
  );

  const confirmReplace = useCallback(() => {
    if (pendingReplace) {
      apply(pendingReplace);
    }

    setPendingReplace(null);
  }, [apply, pendingReplace]);

  const declineReplace = useCallback(() => setPendingReplace(null), []);

  return { place, pendingReplace, confirmReplace, declineReplace };
}
